// Helpers de tracking: UTM + GA4 (gtag/dataLayer) + Meta Pixel (fbq).
// Seguro pra SSR e funciona mesmo se GA4 / Pixel ainda não estiverem instalados.
// Respeita o consentimento LGPD: analytics -> GA4/GTM, marketing -> Meta Pixel.

import { readConsent } from "@/lib/cookie-consent";
import { readAdIdentitySnapshot, readAttributionParams } from "@/lib/tracking";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

export type TrackParams = Record<string, string | number | boolean | undefined>;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Adiciona UTMs a uma URL preservando params existentes da URL de destino
 * e propagando UTMs da URL atual (se houver).
 */
export function withUtm(
  url: string,
  defaults: Partial<Record<(typeof UTM_KEYS)[number], string>> = {},
): string {
  try {
    const target = new URL(url);

    // 1) Propaga UTMs da URL atual (last-touch attribution)
    if (typeof window !== "undefined") {
      const here = new URL(window.location.href);
      for (const k of UTM_KEYS) {
        const v = here.searchParams.get(k);
        if (v && !target.searchParams.has(k)) {
          target.searchParams.set(k, v);
        }
      }
    }

    // 2) Aplica defaults (só se ainda não existir)
    for (const [k, v] of Object.entries(defaults)) {
      if (v && !target.searchParams.has(k)) {
        target.searchParams.set(k, v);
      }
    }

    return target.toString();
  } catch {
    return url;
  }
}

/**
 * Enriquece o payload do evento com UTMs e click IDs (gclid, fbclid, msclkid,
 * ttclid, wbraid, gbraid) capturados da URL/sessão, sem sobrescrever valores
 * explícitos passados pelo chamador (ex.: `source`).
 */
export function withAttribution(params: TrackParams = {}): TrackParams {
  const attribution = readAttributionParams();
  const merged: TrackParams = { ...attribution, ...params };
  // `source` explícito manda; senão deriva do utm_source quando existir.
  if (merged.source === undefined && attribution.utm_source) {
    merged.source = attribution.utm_source;
  }
  return merged;
}

/** Dispara evento no GA4 (gtag + dataLayer) e no Meta Pixel, respeitando consentimento. */
export function trackEvent(eventName: string, params: TrackParams = {}): void {
  if (typeof window === "undefined") return;

  const consent = readConsent();
  const analyticsAllowed = consent?.analytics === true;
  const marketingAllowed = consent?.marketing === true;

  // Sem consentimento (ainda não decidiu ou negou): não dispara nada.
  if (!analyticsAllowed && !marketingAllowed) return;

  const enriched = withAttribution(params);

  if (analyticsAllowed) {
    // GA4 via gtag
    try {
      window.gtag?.("event", eventName, enriched);
    } catch {
      // noop
    }

    // GTM / dataLayer
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...enriched });
    } catch {
      // noop
    }
  }

  if (marketingAllowed) {
    // Meta Pixel — usa trackCustom pra nomes próprios
    try {
      window.fbq?.("trackCustom", eventName, enriched);
    } catch {
      // noop
    }
  }
}

/** Dados de identificação opcionais para melhorar a match quality na CAPI. */
export type MetaUserData = {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  country?: string;
  external_id?: string;
};

function genEventId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    /* noop */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readFbCookie(name: "_fbp" | "_fbc"): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Identificadores de clique da Meta. Quando o Pixel ainda não carregou (sem
 * consentimento de marketing) os cookies _fbp/_fbc não existem, então o _fbc é
 * derivado do fbclid capturado da URL/sessão — é o que liga o lead à campanha.
 */
function readMetaClickIds(): { fbp?: string; fbc?: string } {
  const fbp = readFbCookie("_fbp");
  let fbc = readFbCookie("_fbc");
  if (!fbc) {
    try {
      const snap = readAdIdentitySnapshot();
      fbc = snap.fbc || undefined;
    } catch {
      /* noop */
    }
  }
  return { fbp, fbc };
}

function sendMetaCapi(
  eventName: string,
  eventId: string,
  params: TrackParams,
  userData?: MetaUserData,
): void {
  if (typeof window === "undefined") return;
  const { fbp, fbc } = readMetaClickIds();
  const body = {
    event_name: eventName,
    event_id: eventId,
    event_source_url: window.location.href,
    action_source: "website" as const,
    user_data: {
      ...(userData ?? {}),
      fbp,
      fbc,
    },
    custom_data: params,
  };
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    // sendBeacon é resiliente em navegações; fallback para fetch keepalive.
    if (navigator.sendBeacon?.("/api/public/meta-capi", blob)) return;
  } catch {
    /* noop */
  }
  try {
    void fetch("/api/public/meta-capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* noop */
  }
}

/**
 * Conversão real de lead (formulário enviado pelo próprio usuário).
 *
 * Diferente de `trackMetaPixel`, o envio server-side (Conversions API) acontece
 * sempre — o dado é fornecido voluntariamente no formulário para a finalidade do
 * contato. O evento no navegador (fbq) continua dependendo do consentimento de
 * marketing, e o `event_id` deduplica os dois caminhos.
 */
export function trackMetaLead(
  params: TrackParams = {},
  userData?: MetaUserData,
  eventName = "Lead",
): void {
  if (typeof window === "undefined") return;
  const eventId = genEventId();
  const enriched = withAttribution(params);
  if (readConsent()?.marketing === true) {
    try {
      window.fbq?.("track", eventName, enriched, { eventID: eventId });
    } catch {
      /* noop */
    }
  }
  sendMetaCapi(eventName, eventId, enriched, userData);
}

/** Dispara um evento padrão do Meta Pixel + CAPI (deduplicado por event_id). */
export function trackMetaPixel(
  eventName: string,
  params: TrackParams = {},
  userData?: MetaUserData,
): void {
  if (typeof window === "undefined") return;
  const consent = readConsent();
  if (consent?.marketing !== true) return;
  const eventId = genEventId();
  const enriched = withAttribution(params);
  try {
    window.fbq?.("track", eventName, enriched, { eventID: eventId });
  } catch {
    // noop
  }
  sendMetaCapi(eventName, eventId, enriched, userData);
}

/** Dispara um evento CUSTOMIZADO do Meta Pixel (trackCustom) + CAPI. */
export function trackMetaPixelCustom(
  eventName: string,
  params: TrackParams = {},
  userData?: MetaUserData,
): void {
  if (typeof window === "undefined") return;
  const consent = readConsent();
  if (consent?.marketing !== true) return;
  const eventId = genEventId();
  const enriched = withAttribution(params);
  try {
    window.fbq?.("trackCustom", eventName, enriched, { eventID: eventId });
  } catch {
    // noop
  }
  sendMetaCapi(eventName, eventId, enriched, userData);
}


/**
 * Dispara evento padronizado de clique em entradas para a página /ia
 * (header, footer, cards de segmento, mobile sticky, página de segmento).
 *
 * `location` identifica de onde partiu o clique. `segment` é opcional e
 * deve conter o slug/nome do segmento quando aplicável.
 */
export function trackIaNav(
  location:
    | "header_desktop"
    | "header_mobile"
    | "footer"
    | "mobile_sticky"
    | "solutions_card"
    | "solutions_bridge_cta"
    | "segment_page_cta"
    | "segment_page_all_ias"
    | "pillars_banner_primary"
    | "pillars_banner_secondary"
    | "clinicas_hero"
    | "clinicas_ia_block",
  segment?: string,
  extra: TrackParams = {},
): void {
  trackEvent("ia_nav_click", {
    location,
    segment: segment ?? "none",
    ...extra,
  });
}

/* ------------------------------------------------------------------ */
/* Pixel de anúncios do ChatGPT (OpenAI) — evento de lead             */
/* ------------------------------------------------------------------ */

type OaiqWindow = { oaiq?: (...args: unknown[]) => void };

/** Nome padrão de conversão reconhecido pelo ChatGPT Ads. */
export const OPENAI_LEAD_EVENT = "registration_completed";

/**
 * Dispara uma conversão no pixel do ChatGPT Ads (client-side) e envia o mesmo
 * evento pela Conversions API (server-side), deduplicado pelo `id`.
 * Respeita o consentimento de marketing (LGPD); se ainda não houver consentimento
 * o evento fica em fila na sessão e é reprocessado quando o usuário aceitar.
 */
export function trackOpenAiConversion(
  eventName: string = OPENAI_LEAD_EVENT,
  data: Record<string, unknown> = { type: "customer_action", amount: 0, currency: "USD" },
): void {
  if (typeof window === "undefined") return;
  const eventId = genEventId();
  // O SDK valida estritamente o payload: apenas type/amount/currency.
  // Qualquer chave extra (inclusive event_id) faz o evento ser descartado
  // com "invalid_event_props" — o event_id vai só para a Conversions API.
  const payload = {
    type: "customer_action",
    amount: typeof data.amount === "number" ? data.amount : 0,
    currency: typeof data.currency === "string" ? data.currency : "USD",
  };


  if (readConsent()?.marketing !== true) {
    try {
      const key = "kentro-oaiq-pending";
      const pending = JSON.parse(window.sessionStorage.getItem(key) || "[]") as unknown[];
      pending.push({ event: eventName, data: payload });
      window.sessionStorage.setItem(key, JSON.stringify(pending.slice(-10)));
    } catch {
      /* noop */
    }
    return;
  }

  try {
    (window as unknown as OaiqWindow).oaiq?.("measure", eventName, payload);
  } catch {
    /* noop */
  }

  // Server-side (Conversions API) — mesmo event_id para deduplicar.
  const body = JSON.stringify({
    event_id: eventId,
    event_name: eventName,
    source_url: window.location.href,
    data: { type: "customer_action", amount: payload.amount, currency: payload.currency },
  });
  try {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon?.("/api/public/openai-capi", blob)) return;
  } catch {
    /* noop */
  }
  try {
    void fetch("/api/public/openai-capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
  } catch {
    /* noop */
  }
}

/**
 * Conversão de lead criado (qualquer formulário do site).
 * Usa o nome de evento padrão do ChatGPT Ads para que apareça no painel;
 * a origem do formulário segue no GA4/Meta, não no payload do pixel.
 */
export function trackLeadCreated(formSource: string): void {
  trackEvent("openai_lead_conversion", { form_source: formSource });
  trackOpenAiConversion(OPENAI_LEAD_EVENT, {
    type: "customer_action",
    amount: 0,
    currency: "USD",
  });
}

